import { z } from 'zod';
import { ProcedureIdSchema, UserIdSchema } from './common';

/**
 * Tipo de archivo de resultado. La distinción es lógica (no se valida por
 * mimeType acá) — el backend infiere `pdf` o `image` a partir del mimeType
 * o del nombre original. Útil para que el portal renderice miniaturas vs
 * previsualizar PDFs.
 */
export const ResultKindSchema = z.enum(['pdf', 'image']);
export type ResultKind = z.infer<typeof ResultKindSchema>;

/**
 * Mime types aceptados al subir un resultado. Mantenemos la lista corta y
 * controlada para evitar que se cuelen ejecutables o documentos no clínicos.
 */
export const AllowedResultMimeTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;

/**
 * DTO devuelto al listar resultados de un procedimiento (sin el binario —
 * el binario se obtiene vía endpoint dedicado de descarga).
 */
export const ProcedureResultSchema = z.object({
  id: z.string().uuid(),
  procedimientoId: ProcedureIdSchema,
  tipo: ResultKindSchema,
  nombreOriginal: z.string().min(1).max(255),
  mimeType: z.string().min(3).max(120),
  tamanoBytes: z.number().int().min(0),
  descripcion: z.string().max(500).optional(),
  subidoPorId: UserIdSchema,
  subidoPorNombre: z.string().optional(),
  subidoEn: z.string().datetime(),
});
export type ProcedureResult = z.infer<typeof ProcedureResultSchema>;

/**
 * Body de `POST /api/procedures/:id/results` — recibe el archivo codificado
 * en base64. Para el prototipo evita configurar multipart en Fastify; cuando
 * se migre a S3-direct-upload solo cambia el adapter `FileStorage`.
 *
 * Tope de tamaño: 24 MB binarios (~32 MB base64) para mantener el
 * bodyLimit del servidor cómodo.
 */
export const UploadResultSchema = z.object({
  nombreOriginal: z.string().min(1).max(255),
  mimeType: z.enum(AllowedResultMimeTypes),
  contenidoBase64: z.string().min(8).max(34 * 1024 * 1024),
  descripcion: z.string().max(500).optional(),
});
export type UploadResultInput = z.infer<typeof UploadResultSchema>;

export const ProcedureResultListSchema = z.object({
  items: z.array(ProcedureResultSchema),
});
export type ProcedureResultList = z.infer<typeof ProcedureResultListSchema>;

/* -------------------------------------------------------------------------- */
/* Subida directa al bucket (presigned)                                        */
/* -------------------------------------------------------------------------- */

/**
 * Tope de tamaño para subida directa. Mucho mayor que el del endpoint base64
 * (24 MB) porque el archivo NO pasa por la API ni por su bodyLimit.
 */
export const MAX_RESULT_UPLOAD_BYTES = 100 * 1024 * 1024;

/** Body de `POST /api/procedures/:id/results/presign`. */
export const PresignResultUploadSchema = z.object({
  nombreOriginal: z.string().min(1).max(255),
  mimeType: z.enum(AllowedResultMimeTypes),
  /** Tamaño declarado; se contrasta contra el real en la confirmación. */
  tamanoBytes: z.number().int().positive().max(MAX_RESULT_UPLOAD_BYTES),
});
export type PresignResultUploadInput = z.infer<typeof PresignResultUploadSchema>;

/**
 * Respuesta del presign. El cliente hace `PUT uploadUrl` con EXACTAMENTE el
 * header `Content-Type` indicado (la firma lo fija: otro valor da 403) y luego
 * llama a confirm con la `key`.
 */
export const PresignedResultUploadSchema = z.object({
  uploadUrl: z.string().url(),
  key: z.string().min(1),
  /** Headers obligatorios en el PUT. */
  headers: z.record(z.string()),
  expiraEn: z.string().datetime(),
});
export type PresignedResultUpload = z.infer<typeof PresignedResultUploadSchema>;

/**
 * Body de `POST /api/procedures/:id/results/confirm`. La API verifica que el
 * objeto exista, su tamaño real y sus magic bytes ANTES de registrarlo; si algo
 * no cuadra, borra el objeto y rechaza.
 */
export const ConfirmResultUploadSchema = z.object({
  key: z.string().min(1).max(512),
  nombreOriginal: z.string().min(1).max(255),
  mimeType: z.enum(AllowedResultMimeTypes),
  descripcion: z.string().max(500).optional(),
});
export type ConfirmResultUploadInput = z.infer<typeof ConfirmResultUploadSchema>;

/* -------------------------------------------------------------------------- */
/* Portal del paciente — endpoints públicos                                   */
/* -------------------------------------------------------------------------- */

/** Body de `POST /api/portal/login`. */
export const PortalLoginSchema = z.object({
  documento: z.string().min(3).max(20),
  // La "contraseña" del portal ES la cédula del paciente (decisión de producto),
  // así que debe tener el MISMO rango que el documento. Un mínimo de 8 dejaba
  // fuera a pacientes con cédula de 7 dígitos o menos (p. ej. 8355150).
  password: z.string().min(3).max(20),
});
export type PortalLoginInput = z.infer<typeof PortalLoginSchema>;

/** Respuesta de `POST /api/portal/login`. */
export const PortalSessionSchema = z.object({
  token: z.string(),
  paciente: z.object({
    id: z.string(),
    nombres: z.string(),
    apellidos: z.string(),
    documento: z.string(),
  }),
  expiraEn: z.string().datetime(),
});
export type PortalSession = z.infer<typeof PortalSessionSchema>;

/** Item devuelto por `GET /api/portal/me/procedures`. */
export const PortalProcedureItemSchema = z.object({
  id: ProcedureIdSchema,
  fecha: z.string().date(),
  hora: z.string(),
  tipo: z.string(),
  medicoNombre: z.string().optional(),
  resultados: z.array(ProcedureResultSchema),
});
export type PortalProcedureItem = z.infer<typeof PortalProcedureItemSchema>;

export const PortalProcedureListSchema = z.object({
  items: z.array(PortalProcedureItemSchema),
});
export type PortalProcedureList = z.infer<typeof PortalProcedureListSchema>;
